import { z } from "zod";
import { localDateSchema, moneySchema, quantitySchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
import { fiscalDeviceScopeSchema, dayClosingStatusSchema, shiftClosingWarningSchema, shiftMemberDutySchema, shiftStatusSchema, } from "./enums.js";
import { dayCollectionTotalSchema, dayProductTotalSchema } from "./day-end.js";
/**
 * Vardiya ve gün sonu raporları (Prompt 13 — API C aşaması).
 *
 * Ortak kurallar: tüm tutarlar KURUŞ DİZGESİ, litre 3 ondalıklı dizge; tarih süzgeçleri İŞ GÜNÜNE göredir
 * (takvim gününe değil — gece yarısını aşan vardiya başladığı iş gününde sayılır). İstasyon kapsamı
 * `visibleStationIds` ile daraltılır.
 */
const reportRangeSchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    from: localDateSchema.describe("İş günü (dâhil)"),
    to: localDateSchema.describe("İş günü (dâhil)"),
});
// ── Vardiya raporu ───────────────────────────────────────────────────────────
export const shiftReportMemberSchema = z.object({
    memberId: uuidSchema,
    staffId: uuidSchema,
    staffCode: z.string(),
    /** KVKK: kişisel veri — yalnızca yetkili okuma yanıtında döner. */
    staffName: z.string(),
    duty: shiftMemberDutySchema,
    expectedAmount: moneySchema,
    declaredTotal: moneySchema,
    difference: moneySchema,
    withinTolerance: z.boolean(),
    status: z.string(),
    warnings: z.array(shiftClosingWarningSchema),
});
export const shiftReportSchema = z.object({
    shiftId: uuidSchema,
    shiftNo: z.string(),
    name: z.string(),
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    businessDate: localDateSchema,
    status: shiftStatusSchema,
    openedAt: timestampSchema,
    closingStartedAt: timestampSchema.nullable(),
    expectedAmount: moneySchema,
    declaredTotal: moneySchema,
    shortage: moneySchema,
    surplus: moneySchema,
    saleLitres: quantitySchema,
    byProduct: z.array(dayProductTotalSchema),
    collections: z.array(dayCollectionTotalSchema),
    members: z.array(shiftReportMemberSchema),
    records: z.object({
        cashDropTotal: moneySchema,
        cashDropCount: z.number().int().nonnegative(),
        cashExpenseTotal: moneySchema,
        cashExpenseCount: z.number().int().nonnegative(),
        creditSaleTotal: moneySchema,
        creditSaleCount: z.number().int().nonnegative(),
        testDrawLitres: quantitySchema,
        testDrawCount: z.number().int().nonnegative(),
    }),
    warnings: z.array(z.string()),
});
// ── Pompacı performansı ──────────────────────────────────────────────────────
export const attendantPerformanceRowSchema = z.object({
    staffId: uuidSchema,
    staffCode: z.string(),
    staffName: z.string(),
    stationId: uuidSchema,
    stationName: z.string(),
    shiftCount: z.number().int().nonnegative(),
    saleLitres: quantitySchema,
    expectedAmount: moneySchema.describe("Ciro (KDV dâhil beklenen tutar)"),
    shortageCount: z.number().int().nonnegative(),
    shortageTotal: moneySchema,
    surplusCount: z.number().int().nonnegative(),
    surplusTotal: moneySchema,
    /** Ortalama fark (işaretli, kuruş; tam bölünmeyen kısım aşağı yuvarlanır). */
    averageDifference: moneySchema,
    lastDifferences: z
        .array(z.object({ businessDate: localDateSchema, shiftNo: z.string(), difference: moneySchema }))
        .describe("Son 10 fark (yeniden eskiye)"),
});
export const attendantPerformanceSchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    items: z.array(attendantPerformanceRowSchema),
});
export const attendantPerformanceQuerySchema = reportRangeSchema;
// ── Açık / fazla trendi ──────────────────────────────────────────────────────
export const DIFFERENCE_GROUP_BY = ["day", "staff", "station"];
export const differenceGroupBySchema = z.enum(DIFFERENCE_GROUP_BY);
export const differenceTrendRowSchema = z.object({
    key: z.string().describe("Gruplama anahtarı: iş günü, personel kimliği ya da istasyon kimliği"),
    label: z.string(),
    closingCount: z.number().int().nonnegative(),
    expectedAmount: moneySchema,
    shortageCount: z.number().int().nonnegative(),
    shortageTotal: moneySchema,
    surplusCount: z.number().int().nonnegative(),
    surplusTotal: moneySchema,
    netDifference: moneySchema.describe("fazla − açık (işaretli)"),
});
export const differenceTrendSchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    groupBy: differenceGroupBySchema,
    items: z.array(differenceTrendRowSchema),
    total: differenceTrendRowSchema,
});
export const differenceTrendQuerySchema = reportRangeSchema.extend({
    groupBy: differenceGroupBySchema.default("day"),
});
// ── Z mutabakatı ─────────────────────────────────────────────────────────────
export const zReconciliationRowSchema = z.object({
    businessDate: localDateSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    fiscalDeviceId: uuidSchema,
    serialNo: z.string(),
    label: z.string(),
    scope: fiscalDeviceScopeSchema,
    zNo: z.number().int().nullable(),
    zAmount: moneySchema.nullable(),
    systemAmount: moneySchema.nullable(),
    difference: moneySchema.nullable(),
    /** `MISSING` (Z girilmemiş) · `OK` · `MISMATCH` · `RESOLVED`. */
    status: z.enum(["MISSING", "OK", "MISMATCH", "RESOLVED"]),
});
export const zReconciliationSchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    items: z.array(zReconciliationRowSchema),
});
export const zReconciliationQuerySchema = reportRangeSchema;
// ── Gün sonu raporu (istasyon + zincir) ──────────────────────────────────────
export const dayEndReportRowSchema = z.object({
    businessDate: localDateSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    status: dayClosingStatusSchema,
    shiftCount: z.number().int().nonnegative(),
    saleLitres: quantitySchema,
    expectedAmount: moneySchema.describe("Ciro (KDV dâhil)"),
    shortage: moneySchema,
    surplus: moneySchema,
    collections: z.array(dayCollectionTotalSchema),
});
export const dayEndReportSchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    items: z.array(dayEndReportRowSchema),
    /** ZİNCİR toplamı: seçilen aralıktaki tüm istasyonların toplamı. */
    total: z.object({
        stationCount: z.number().int().nonnegative(),
        dayCount: z.number().int().nonnegative(),
        closedDayCount: z.number().int().nonnegative(),
        shiftCount: z.number().int().nonnegative(),
        saleLitres: quantitySchema,
        expectedAmount: moneySchema,
        shortage: moneySchema,
        surplus: moneySchema,
        collections: z.array(dayCollectionTotalSchema),
    }),
});
export const dayEndReportQuerySchema = reportRangeSchema;
//# sourceMappingURL=reports.js.map